import { useEffect } from 'react'
import * as React from 'react'
import { useBlocker } from 'react-router-dom'
import { useDirtyStore } from '@/modules/shared/stores/dirtyStore'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DiffView } from '@/modules/shared/components/DiffView'

type Props = {
  when: boolean
  message?: string
}

export const UnsavedChangesPrompt: React.FC<Props> = ({ when }) => {
  const confirm = useConfirm()
  const { t } = useLocalize()
  const diffEntries = useDirtyStore((s) => s.diffEntries)
  const blocker = useBlocker(when)

  React.useEffect(() => {
    if (blocker.state !== 'blocked') return
    const hasDiff = diffEntries.length > 0
    confirm({
      title: t('configuration.unsaved-changes-title'),
      message: t('configuration.unsaved-changes-confirm'),
      content: hasDiff ? <DiffView entries={diffEntries} /> : undefined,
      confirmText: t('configuration.unsaved-changes-leave'),
      cancelText: t('configuration.unsaved-changes-stay'),
      classNamePopupContent: hasDiff ? 'tw-w-[700px]' : 'tw-w-[500px]',
    }).then((ok) => {
      if (ok) blocker.proceed()
      else blocker.reset()
    })
  }, [blocker.state])

  return null
}

export const useBeforeUnloadPrompt = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return () => {}

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [enabled])
}

export const useUnsavedChangesWarning = () => {
  const isDirty = useDirtyStore((s) => s.isDirty)

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])
}
